import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Package, Minus, Plus as PlusIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function PartsPage() {
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", quantity: 0, low_stock_threshold: 5, unit_cost: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/parts");
      setParts(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/parts", {
        ...form,
        name: form.name.trim(),
        quantity: Number(form.quantity) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      });
      toast.success("Part added");
      setForm({ name: "", sku: "", quantity: 0, low_stock_threshold: 5, unit_cost: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const adjustQuantity = async (part, delta) => {
    const newQty = Math.max(0, part.quantity + delta);
    try {
      await api.patch(`/parts/${part.id}`, { quantity: newQty });
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this part?")) return;
    try {
      await api.delete(`/parts/${id}`);
      toast.success("Part removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  const lowStockCount = parts.filter((p) => p.quantity <= p.low_stock_threshold).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Parts inventory</h1>
            <p className="text-sm text-gray-600 mt-2">
              Track common spares so you never run out mid-job.
              {lowStockCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle size={12} /> {lowStockCount} low stock
                </span>
              )}
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] h-11" data-testid="add-part-btn">
                <Plus size={16} className="mr-1" /> New part
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl">New part</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                    <Input
                      data-testid="part-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="HVAC filter 16x20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">SKU</Label>
                    <Input
                      data-testid="part-sku"
                      value={form.sku}
                      onChange={(e) => setForm({ ...form, sku: e.target.value })}
                      placeholder="optional"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Qty</Label>
                    <Input
                      data-testid="part-qty"
                      type="number" min="0"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Low at</Label>
                    <Input
                      data-testid="part-threshold"
                      type="number" min="0"
                      value={form.low_stock_threshold}
                      onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Unit $</Label>
                    <Input
                      data-testid="part-cost"
                      type="number" step="0.01" min="0"
                      value={form.unit_cost}
                      onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Notes</Label>
                  <Input
                    data-testid="part-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-part" className="bg-[#0055FF] hover:bg-[#0044CC]" disabled={saving} onClick={create}>
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="parts-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-center">Quantity</TableHead>
                <TableHead>Unit cost</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {parts.map((p) => {
                const low = p.quantity <= p.low_stock_threshold;
                return (
                  <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${low ? "bg-amber-50 text-amber-600" : "bg-[#0055FF]/10 text-[#0055FF]"}`}>
                          <Package size={14} />
                        </div>
                        <div>
                          <div>{p.name}</div>
                          {p.notes && <div className="text-xs text-gray-500">{p.notes}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600 font-mono text-xs">{p.sku || "—"}</TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="icon" variant="ghost"
                          data-testid={`part-decrement-${p.id}`}
                          onClick={() => adjustQuantity(p, -1)}
                          disabled={p.quantity === 0}
                          className="h-7 w-7"
                        >
                          <Minus size={12} />
                        </Button>
                        <span className={`tabular-nums w-12 text-center font-semibold ${low ? "text-amber-600" : ""}`}>
                          {p.quantity}
                        </span>
                        <Button
                          size="icon" variant="ghost"
                          data-testid={`part-increment-${p.id}`}
                          onClick={() => adjustQuantity(p, 1)}
                          className="h-7 w-7"
                        >
                          <PlusIcon size={12} />
                        </Button>
                      </div>
                      {low && (
                        <div className="text-[10px] uppercase tracking-wider text-amber-600 mt-0.5">
                          Low (≤ {p.low_stock_threshold})
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600 tabular-nums">
                      {p.unit_cost != null ? `$${Number(p.unit_cost).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="icon"
                        data-testid={`delete-part-${p.id}`}
                        onClick={() => remove(p.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {parts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">
                    No parts tracked yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
